/**
 * FormA.jsx — CBAM Compliance Form A
 * Sheet: A_InstData
 *
 * Jul 2026 revision — Creation-mode redesign:
 *   • Route `/form-a/new` puts Form A into "creation mode":
 *       - Sidebar is hidden
 *       - Tabs 02–05 are disabled with a tooltip
 *       - Only a "Save & Continue" button is shown (no per-tab NEXT)
 *       - Clicking Save & Continue creates the parent `auditReports/{id}`
 *         doc AND the `form_a/main` subcollection doc in a single flow,
 *         then navigates to `/form-a/{newId}` (replace: true) so the user
 *         stays in Form A with all tabs unlocked, landing on Installation.
 *   • Audit-level metadata (name, description, reportingFrequency) now
 *     lives at the top of Tab 1 as a new "Audit identification" section.
 *     These are required in creation mode; editable freely afterwards.
 *   • Existing tab 1 fields, table logic, validation and Firestore sync
 *     are unchanged.
 *
 * Prior revision (Jun 2026) items 1–10 still preserved.
 */

import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  getDefaultAnnualPeriod,
  getQuarterPeriod,
  validateReportingPeriod,
  isLeapYear,
} from '../utils/reportingPeriod';
import { Box, Tooltip, Radio, RadioGroup, FormControlLabel, FormControl, Autocomplete, TextField } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { firestore as db } from './firebase.config';
import { doc, setDoc, onSnapshot, getDoc, serverTimestamp } from 'firebase/firestore';
import { useFormContext } from '../context/FormContext';
import { useAuth } from '../auth/AuthContext';
import CountryAutocomplete from './CountryAutocomplete';
import AuditSidebar from "../components/AuditSidebar";
import useAuditProgress from "../hooks/useAuditProgress";
import {
  calculateRequiredProgress,
  calculateRequiredProgressForRows,
  FORM_A_REQUIRED_FIELDS,
} from "../utils/CbamProgress";

//  UNLOCODE coordinates lookup (~170 codes; extend by editing the JSON)
import UNLOCODE_DB from "../data/unlocode-coords.json";

//  CN codes lookup (~3000 codes; loaded as side-import, never inlined)
import CN_CODES_DB from "../data/cnCodes.json";
import { getCnLabel } from "../utils/cnCodeLookup";

// ─── Phone country codes (embedded — covers ~60 most common) ─────────────────
const PHONE_CODES = [
  { code: '+91',   iso: 'IN', name: 'India' },
  { code: '+1',    iso: 'US', name: 'United States / Canada' },
  { code: '+44',   iso: 'GB', name: 'United Kingdom' },
  { code: '+49',   iso: 'DE', name: 'Germany' },
  { code: '+33',   iso: 'FR', name: 'France' },
  { code: '+39',   iso: 'IT', name: 'Italy' },
  { code: '+34',   iso: 'ES', name: 'Spain' },
  { code: '+31',   iso: 'NL', name: 'Netherlands' },
  { code: '+32',   iso: 'BE', name: 'Belgium' },
  { code: '+351',  iso: 'PT', name: 'Portugal' },
  { code: '+30',   iso: 'GR', name: 'Greece' },
  { code: '+48',   iso: 'PL', name: 'Poland' },
  { code: '+420',  iso: 'CZ', name: 'Czech Republic' },
  { code: '+43',   iso: 'AT', name: 'Austria' },
  { code: '+36',   iso: 'HU', name: 'Hungary' },
  { code: '+421',  iso: 'SK', name: 'Slovakia' },
  { code: '+40',   iso: 'RO', name: 'Romania' },
  { code: '+359',  iso: 'BG', name: 'Bulgaria' },
  { code: '+45',   iso: 'DK', name: 'Denmark' },
  { code: '+46',   iso: 'SE', name: 'Sweden' },
  { code: '+358',  iso: 'FI', name: 'Finland' },
  { code: '+47',   iso: 'NO', name: 'Norway' },
  { code: '+353',  iso: 'IE', name: 'Ireland' },
  { code: '+41',   iso: 'CH', name: 'Switzerland' },
  { code: '+86',   iso: 'CN', name: 'China' },
  { code: '+852',  iso: 'HK', name: 'Hong Kong' },
  { code: '+81',   iso: 'JP', name: 'Japan' },
  { code: '+82',   iso: 'KR', name: 'South Korea' },
  { code: '+65',   iso: 'SG', name: 'Singapore' },
  { code: '+60',   iso: 'MY', name: 'Malaysia' },
  { code: '+84',   iso: 'VN', name: 'Vietnam' },
  { code: '+66',   iso: 'TH', name: 'Thailand' },
  { code: '+62',   iso: 'ID', name: 'Indonesia' },
  { code: '+63',   iso: 'PH', name: 'Philippines' },
  { code: '+92',   iso: 'PK', name: 'Pakistan' },
  { code: '+880',  iso: 'BD', name: 'Bangladesh' },
  { code: '+94',   iso: 'LK', name: 'Sri Lanka' },
  { code: '+971',  iso: 'AE', name: 'UAE' },
  { code: '+966',  iso: 'SA', name: 'Saudi Arabia' },
  { code: '+974',  iso: 'QA', name: 'Qatar' },
  { code: '+968',  iso: 'OM', name: 'Oman' },
  { code: '+965',  iso: 'KW', name: 'Kuwait' },
  { code: '+90',   iso: 'TR', name: 'Turkey' },
  { code: '+20',   iso: 'EG', name: 'Egypt' },
  { code: '+27',   iso: 'ZA', name: 'South Africa' },
  { code: '+234',  iso: 'NG', name: 'Nigeria' },
  { code: '+254',  iso: 'KE', name: 'Kenya' },
  { code: '+212',  iso: 'MA', name: 'Morocco' },
  { code: '+7',    iso: 'RU', name: 'Russia' },
  { code: '+380',  iso: 'UA', name: 'Ukraine' },
  { code: '+55',   iso: 'BR', name: 'Brazil' },
  { code: '+52',   iso: 'MX', name: 'Mexico' },
  { code: '+54',   iso: 'AR', name: 'Argentina' },
  { code: '+56',   iso: 'CL', name: 'Chile' },
  { code: '+57',   iso: 'CO', name: 'Colombia' },
  { code: '+61',   iso: 'AU', name: 'Australia' },
  { code: '+64',   iso: 'NZ', name: 'New Zealand' },
  { code: '+972',  iso: 'IL', name: 'Israel' },
];

// ─── CBAM Reference Data ─────────────────────────────────────────────────────

const CBAM_GOODS = [
  { id: 'G1',  name: 'Cement',                       isComplexGood: true,  routes: ['All production routes'], precursors: ['Cement clinker', 'Calcined clays'] },
  { id: 'G2',  name: 'Cement clinker',               isComplexGood: false, routes: ['All production routes'], precursors: [] },
  { id: 'G3',  name: 'Calcined clays',               isComplexGood: false, routes: ['All production routes'], precursors: [] },
  { id: 'G4',  name: 'Aluminous cement',             isComplexGood: false, routes: ['All production routes'], precursors: [] },
  { id: 'G5',  name: 'Iron or steel products',       isComplexGood: true,  routes: ['All production routes'], precursors: ['Crude steel', 'Pig iron', 'Direct reduced iron'] },
  { id: 'G6',  name: 'Crude steel',                  isComplexGood: true,  routes: ['Basic oxygen steelmaking', 'Electric arc furnace', 'Other production routes', 'Unknown production routes'], precursors: ['Pig iron', 'Direct reduced iron', 'Alloys (FeMn, FeCr, FeNi)'] },
  { id: 'G7',  name: 'Direct reduced iron',          isComplexGood: true,  routes: ['All production routes'], precursors: ['Sintered Ore', 'Hydrogen'] },
  { id: 'G8',  name: 'Pig iron',                     isComplexGood: false, routes: ['Blast furnace route', 'Smelting reduction', 'Other production routes', 'Unknown production routes'], precursors: ['Sintered Ore', 'Alloys (FeMn, FeCr, FeNi)'] },
  { id: 'G9',  name: 'Alloys (FeMn, FeCr, FeNi)',    isComplexGood: false, routes: ['All production routes'], precursors: ['Sintered Ore'] },
  { id: 'G10', name: 'Sintered Ore',                 isComplexGood: false, routes: ['All production routes'], precursors: [] },
  { id: 'G11', name: 'Hydrogen',                     isComplexGood: false, routes: ['Steam reforming and partial oxidation', 'Electrolysis of water', 'Chlor-Alkali electrolysis and production of chlorates', 'Other production routes', 'Unknown production routes'], precursors: [] },
  { id: 'G12', name: 'Ammonia',                      isComplexGood: false, routes: ['Haber-Bosch process with steam reforming of natural gas or biogas', 'Haber-Bosch process with gasification of coal or other fuels', 'Other production routes', 'Unknown production routes'], precursors: ['Hydrogen'] },
  { id: 'G13', name: 'Nitric acid',                  isComplexGood: false, routes: ['All production routes'], precursors: ['Ammonia'] },
  { id: 'G14', name: 'Urea',                         isComplexGood: true,  routes: ['All production routes'], precursors: ['Ammonia'] },
  { id: 'G15', name: 'Mixed fertilisers',            isComplexGood: true,  routes: ['All production routes'], precursors: ['Ammonia', 'Nitric acid', 'Urea'] },
  { id: 'G16', name: 'Aluminium products',           isComplexGood: true,  routes: ['All production routes'], precursors: ['Unwrought aluminium'] },
  { id: 'G17', name: 'Unwrought aluminium',          isComplexGood: false, routes: ['Primary (electrolytic) smelting', 'Secondary melting (recycling)', 'Other production routes', 'Unknown production routes'], precursors: [] },
  { id: 'G18', name: 'Electricity',                  isComplexGood: false, routes: ['All production routes'], precursors: [] },
];

const GOODS_NAMES = CBAM_GOODS.map(g => g.name);
const ROUTES_BY_CATEGORY = Object.fromEntries(CBAM_GOODS.map(g => [g.name, g.routes]));

const CBAM_SECTORS = [
  'Cement', 'Iron and steel', 'Aluminium', 'Fertilisers', 'Hydrogen', 'Electricity',
];

// ─── CN code helpers ─────────────────────────────────────────────────────────
const CN_CODES_BY_GOOD = (() => {
  const idx = {};
  const rows = CN_CODES_DB.cn_codes || [];
  for (const row of rows) {
    const k = row.cbam_good;
    if (!k) continue;
    (idx[k] = idx[k] || []).push(row);
  }
  return idx;
})();

// ─── Tooltip help text ───────────────────────────────────────────────────────

const HELP_ECONOMIC_ACTIVITY =
  "The manufacturing facility's primary industrial activity (NACE-style description). " +
  "Examples: 'Manufacture of cement', 'Production of crude steel', " +
  "'Manufacture of aluminium'. This describes WHAT the manufacturing facility does, " +
  "not the company name.";

const HELP_UNLOCODE =
  "UN/LOCODE — a 5-character code identifying the city or port (2-letter " +
  "country code + 3-letter location code). " +
  "Example: 'INBOM' for Mumbai, 'DEHAM' for Hamburg. " +
  "If a known code is entered, latitude & longitude will auto-fill below.";

const HELP_FREQUENCY =
  "Quarterly reports cover one calendar quarter (used during the CBAM " +
  "transitional phase, 2023–2025). Annual reports cover a full calendar " +
  "year (mandatory from 2026 onwards).";

// ─── Row Factories ────────────────────────────────────────────────────────────

const newGoodsRow = (index) => ({
  _uuid: crypto.randomUUID(),
  id: `G${index + 1}`,
  category: '',
  cnCode: '',
  primaryRoute: '',
  route1: '', route2: '', route3: '', route4: '', route5: '', route6: '',
});

const newProcessRow = (index) => ({
  _uuid: crypto.randomUUID(),
  id: `P${index + 1}`,
  aggregatedGoodsCategory: '',
  inc1: '', inc2: '', inc3: '', inc4: '', inc5: '', inc6: '',
  name: '',
  errorMessage: '',
});

const newPrecursorRow = (index) => ({
  _uuid: crypto.randomUUID(),
  id: `PP${index + 1}`,
  goodsCategory: '',
  linkedProcessId: '',
  countryCode: '',
  cnCode: '',
  route1: '', route2: '', route3: '', route4: '', route5: '',
  name: '',
  error: '',
});

const DEFAULT_STATE = {
  //  NEW: audit-level metadata (persisted to parent auditReports/{id})
  auditName: '',
  auditDescription: '',
  reportingFrequency: '',
  reportingQuarter: '',
  reportingYear: dayjs().year(),
  reportingPeriodStart: null,
  reportingPeriodEnd: null,
  
  organisationType: 'Supplier',
  installationName: '',
  sector: '',
  street: '', economicActivity: '',
  postCode: '', poBox: '', city: '', country: '', unLocode: '',
  latitude: '', longitude: '',
  nameAuthorizedRepresentative: '', email: '',
  phoneCountryCode: '+91',
  telephone: '',

  verifierCompanyName: '', verifierStreet: '', verifierCity: '',
  verifierPostcode: '', verifierCountry: '',
  verifierRepresentativeName: '', verifierEmail: '', verifierTelephone: '', verifierFax: '',
  verifierAccreditationMemberState: '', verifierAccreditationNationalAuthority: '', verifierRegistrationNumber: '',

  aggregatedGoods: [newGoodsRow(0)],
  relevantProcesses: [newProcessRow(0)],

  purchasedPrecursors: [newPrecursorRow(0)],
};

// ─── Firestore Serializer / Deserializer ──────────────────────────────────────
// Note: audit-level metadata (auditName, auditDescription, reportingFrequency)
// is NOT written to form_a/main — it lives on the parent audit doc and is
// written separately in persist() below.

const toFirestore = (fd) => ({
  reportingPeriod: {
    start: fd.reportingPeriodStart?.toDate?.() ?? null,
    end:   fd.reportingPeriodEnd?.toDate?.()   ?? null,
  },
  installation: {
    organisationType: fd.organisationType || 'Supplier',
    name: fd.installationName,
    sector: fd.sector || '',
    street: fd.street, economicActivity: fd.economicActivity,
    postCode: fd.postCode, poBox: fd.poBox, city: fd.city,
    country: fd.country, unLocode: fd.unLocode,
    latitude: fd.latitude, longitude: fd.longitude,
    representative: {
      name: fd.nameAuthorizedRepresentative,
      email: fd.email,
      phoneCountryCode: fd.phoneCountryCode || '+91',
      telephone: fd.telephone,
    },
  },
  verifier: {
    companyName: fd.verifierCompanyName, street: fd.verifierStreet,
    city: fd.verifierCity, postCode: fd.verifierPostcode, country: fd.verifierCountry,
    representative: {
      name: fd.verifierRepresentativeName, email: fd.verifierEmail,
      telephone: fd.verifierTelephone, fax: fd.verifierFax,
    },
    accreditation: {
      memberState: fd.verifierAccreditationMemberState,
      nationalAuthority: fd.verifierAccreditationNationalAuthority,
      registrationNumber: fd.verifierRegistrationNumber,
    },
  },
  aggregatedGoods: (fd.aggregatedGoods || [])
    .filter(r => r.category)
    .map((r, i) => ({
      _uuid: r._uuid || crypto.randomUUID(),
      id: `G${i + 1}`,
      category: r.category,
      cnCode: r.cnCode || '',
      primaryRoute: r.primaryRoute || '',
      routes: [r.route1, r.route2, r.route3, r.route4, r.route5, r.route6].filter(v => v && v.trim()),
    })),
  relevantProcesses: (fd.relevantProcesses || []).map((r, i) => ({
    _uuid: r._uuid || crypto.randomUUID(),
    id: `P${i + 1}`,
    aggregatedGoodsCategory: r.aggregatedGoodsCategory || '',
    includedGoodsRefs: [r.inc1, r.inc2, r.inc3, r.inc4, r.inc5, r.inc6].filter(v => v && v.trim()),
    name: r.name || '',
    errorMessage: r.errorMessage || '',
  })),
  purchasedPrecursors: (fd.purchasedPrecursors || []).map((r, i) => ({
    _uuid: r._uuid || crypto.randomUUID(),
    id: `PP${i + 1}`,
    goodsCategory:   r.goodsCategory   || '',
    linkedProcessId: r.linkedProcessId || '',
    countryCode: r.countryCode || '',
    cnCode: r.cnCode || '',
    routes: [r.route1, r.route2, r.route3, r.route4, r.route5].filter(v => v && v.trim()),
    name: r.name || '',
    error: r.error || '',
  })),
  meta: { updatedAt: new Date() },
});

const fromFirestore = (data, existingState) => {
  const inst = data.installation || {};
  const rep  = inst.representative || {};
  const ver  = data.verifier || {};
  const vrep = ver.representative || {};
  const vacc = ver.accreditation || {};
  const rp   = data.reportingPeriod || {};

  const inflateGoods = (arr = []) => arr.map((g, i) => ({
    _uuid: g._uuid || crypto.randomUUID(), id: `G${i + 1}`,
    category: g.category || '',
    cnCode: g.cnCode || '',
    primaryRoute: g.primaryRoute || '',
    route1: g.routes?.[0] || '', route2: g.routes?.[1] || '',
    route3: g.routes?.[2] || '', route4: g.routes?.[3] || '',
    route5: g.routes?.[4] || '', route6: g.routes?.[5] || '',
  }));

  const inflateProcesses = (arr = []) => arr.map((p, i) => ({
    _uuid: p._uuid || crypto.randomUUID(), id: `P${i + 1}`,
    aggregatedGoodsCategory: p.aggregatedGoodsCategory || '',
    inc1: p.includedGoodsRefs?.[0] || '', inc2: p.includedGoodsRefs?.[1] || '',
    inc3: p.includedGoodsRefs?.[2] || '', inc4: p.includedGoodsRefs?.[3] || '',
    inc5: p.includedGoodsRefs?.[4] || '', inc6: p.includedGoodsRefs?.[5] || '',
    name: p.name || '', errorMessage: p.errorMessage || '',
  }));

  const inflatePrecursors = (arr = []) => arr.map((p, i) => ({
    _uuid: p._uuid || crypto.randomUUID(), id: `PP${i + 1}`,
    goodsCategory:   p.goodsCategory   || '',
    linkedProcessId: p.linkedProcessId || '', countryCode: p.countryCode || '',
    cnCode: p.cnCode || '',
    route1: p.routes?.[0] || '', route2: p.routes?.[1] || '',
    route3: p.routes?.[2] || '', route4: p.routes?.[3] || '',
    route5: p.routes?.[4] || '',
    name: p.name || '', error: p.error || '',
  }));

  const installationName = inst.name || inst.nameEnglish || '';

  return {
    ...existingState,
    reportingPeriodStart: rp.start?.seconds ? dayjs(rp.start.toDate()) : null,
    reportingPeriodEnd:   rp.end?.seconds   ? dayjs(rp.end.toDate())   : null,
    organisationType: inst.organisationType || 'Supplier',
    installationName,
    sector:           inst.sector           || '',
    street:           inst.street           || '',
    economicActivity: inst.economicActivity || '',
    postCode:         inst.postCode         || '',
    poBox:            inst.poBox            || '',
    city:             inst.city             || '',
    country:          inst.country          || '',
    unLocode:         inst.unLocode         || '',
    latitude:         inst.latitude         || '',
    longitude:        inst.longitude        || '',
    nameAuthorizedRepresentative: rep.name             || '',
    email:                        rep.email            || '',
    phoneCountryCode:             rep.phoneCountryCode || '+91',
    telephone:                    rep.telephone        || '',
    verifierCompanyName: ver.companyName || '',
    verifierStreet:      ver.street      || '',
    verifierCity:        ver.city        || '',
    verifierPostcode:    ver.postCode    || '',
    verifierCountry:     ver.country     || '',
    verifierRepresentativeName: vrep.name      || '',
    verifierEmail:              vrep.email     || '',
    verifierTelephone:          vrep.telephone || '',
    verifierFax:                vrep.fax       || '',
    verifierAccreditationMemberState:       vacc.memberState        || '',
    verifierAccreditationNationalAuthority: vacc.nationalAuthority  || '',
    verifierRegistrationNumber:             vacc.registrationNumber || '',
    aggregatedGoods:     (() => { const r = inflateGoods(data.aggregatedGoods);      return r.length ? r : [newGoodsRow(0)];      })(),
    relevantProcesses:   (() => { const r = inflateProcesses(data.relevantProcesses);  return r.length ? r : [newProcessRow(0)];   })(),
    purchasedPrecursors: (() => { const r = inflatePrecursors(data.purchasedPrecursors); return r.length ? r : [newPrecursorRow(0)]; })(),
  };
};

// ─── CSS ──────────────────────────────────────────────────────────────────────

const STYLE = `
  @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@300;400;500;600;700&family=IBM+Plex+Mono:wght@400;500;600&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  .fa-root {
    font-family: 'IBM Plex Sans', sans-serif;
    background: #f8fafc;
    min-height: 100vh;
    width: 100%;
    color: #161616;
    font-size: 14px;
  }

  .fa-header-slim {
    background: #fff;
    border-bottom: 2px solid #2563eb;
    padding: 14px clamp(20px, 3vw, 32px);
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  .fa-header-slim .title {
    font-size: 17px;
    font-weight: 700;
    color: #0f172a;
    letter-spacing: -0.2px;
  }
  .fa-header-slim .sub {
    font-size: 11px;
    color: #64748b;
    margin-top: 2px;
    letter-spacing: 0.4px;
    text-transform: uppercase;
  }

  .fa-body {
    width: 100%;
    max-width: 1280px;
    margin: 0 auto;
    padding: 16px clamp(20px, 3vw, 32px) 24px;
  }

  /*  Creation-mode banner (only shown when creating a new audit) */
  .fa-creation-banner {
    background: linear-gradient(135deg, #7C6EF2 0%, #4A90E2 100%);
    color: #fff;
    padding: 14px 18px;
    border-radius: 10px;
    margin-bottom: 18px;
    display: flex;
    align-items: center;
    gap: 14px;
    box-shadow: 0 6px 16px rgba(74, 108, 226, 0.25);
  }
  .fa-creation-banner .icon {
    width: 36px; height: 36px;
    display: flex; align-items: center; justify-content: center;
    background: rgba(255,255,255,0.18);
    border-radius: 8px;
    font-size: 18px;
    flex-shrink: 0;
  }
  .fa-creation-banner .body { flex: 1; }
  .fa-creation-banner .title-line {
    font-weight: 700; font-size: 14px; letter-spacing: -0.1px; margin-bottom: 2px;
  }
  .fa-creation-banner .sub-line {
    font-size: 12px; opacity: 0.9;
  }

  .fa-tabs {
    display: flex;
    gap: 4px;
    border-bottom: 2px solid #2563eb;
    margin-bottom: 18px;
    overflow-x: auto;
  }
  .fa-tab {
    display: inline-flex;
    align-items: center;
    gap: 9px;
    padding: 9px 16px;
    font-size: 13px;
    color: #64748b;
    cursor: pointer;
    border: 1px solid transparent;
    border-bottom: none;
    background: #fff;
    border-top-left-radius: 6px;
    border-top-right-radius: 6px;
    white-space: nowrap;
    user-select: none;
    transition: background 0.15s, color 0.15s;
  }
  .fa-tab:hover:not(.active):not(.disabled) {
    background: #f1f5f9;
    color: #1e293b;
  }
  .fa-tab.active {
    color: #0f172a;
    font-weight: 700;
    border-color: #cbd5e1;
    background: #fff;
  }
  /*  Disabled tabs in creation mode */
  .fa-tab.disabled {
    opacity: 0.45;
    cursor: not-allowed;
    pointer-events: auto; /* keep tooltip working */
  }
  .fa-tab-num {
    font-family: 'IBM Plex Mono', monospace;
    font-size: 11px;
    font-weight: 700;
    background: #e2e8f0;
    color: #475569;
    padding: 2px 7px;
    border-radius: 4px;
    letter-spacing: 0.5px;
  }
  .fa-tab.active .fa-tab-num {
    background: #2563eb;
    color: #fff;
  }
  .fa-tab-badge {
    padding: 2px 8px;
    border-radius: 99px;
    font-size: 11px;
    font-weight: 700;
    background: #E0E0E0;
    color: #555;
  }
  .fa-tab-badge.done { background: #DCFCE7; color: #15803D; }
  .fa-tab-badge.warn { background: #FEF3C7; color: #D97706; }
  .fa-tab-badge.empty { background: #FEE2E2; color: #DC2626; }
  .fa-tab-lock {
    font-size: 12px;
    color: #94a3b8;
  }

  .fa-tab-panel {
    background: #fff;
    border: 1px solid #cbd5e1;
    border-top: none;
    border-radius: 0 0 8px 8px;
    padding: 20px 18px;
    margin-top: -18px;
    margin-bottom: 18px;
    box-shadow: 0 2px 6px rgba(0,0,0,0.04);
    animation: fadeIn 0.18s ease;
  }
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(-3px); }
    to { opacity: 1; transform: none; }
  }

  .fa-subsection { margin-bottom: 20px; }
  .fa-subsection:last-child { margin-bottom: 0; }

  .fa-sub-label {
    font-size: 12px;
    font-weight: 700;
    color: #1e293b;
    margin-bottom: 12px;
    padding-bottom: 5px;
    border-bottom: 2px solid #2563eb;
    display: inline-block;
  }

  .fa-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px 16px; }
  .fa-grid-3 { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 12px 16px; }
  .fa-grid-full { grid-column: 1 / -1; }

  .fa-field { display: flex; flex-direction: column; gap: 5px; }

  .fa-label {
    font-size: 11px;
    font-weight: 600;
    color: #475569;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    display: inline-flex;
    align-items: center;
    gap: 5px;
  }
  .fa-req-star {
    color: #2563eb;
    font-weight: 700;
    margin-left: 2px;
  }
  .fa-label-help {
    color: #94a3b8;
    cursor: help;
    display: inline-flex;
    align-items: center;
  }
  .fa-label-help:hover { color: #2563eb; }

  .fa-input, .fa-select, .fa-textarea {
    border: 1px solid #cbd5e1;
    padding: 8px 10px;
    font-family: 'IBM Plex Sans', sans-serif;
    font-size: 14px;
    color: #161616;
    background: #f8fafc;
    border-radius: 4px;
    outline: none;
    transition: border-color 0.15s, background 0.15s;
    width: 100%;
  }
  .fa-textarea { resize: vertical; min-height: 60px; font-family: 'IBM Plex Sans', sans-serif; }
  .fa-input:focus, .fa-select:focus, .fa-textarea:focus {
    border-color: #2563eb;
    background: #fff;
  }
  .fa-input[readonly] { background: #eef2ff; cursor: default; }
  .fa-select { cursor: pointer; }

  .fa-charcount {
    font-size: 10px;
    color: #94a3b8;
    text-align: right;
    margin-top: 2px;
  }

  .fa-date-row { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 14px; }
  .fa-date-row-2col { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }

  .fa-phone-row {
    display: grid;
    grid-template-columns: 110px 1fr;
    gap: 6px;
  }
  .fa-phone-row .fa-select { padding-right: 4px; }

  .fa-orgtype-box {
    border: 1px solid #cbd5e1;
    background: #f8fafc;
    border-radius: 6px;
    padding: 10px 14px;
  }

  .fa-auto-tag {
    font-size: 10px;
    font-weight: 700;
    color: #15803D;
    background: #DCFCE7;
    padding: 2px 7px;
    border-radius: 3px;
    margin-left: 4px;
  }

  .fa-divider {
    border: none;
    border-top: 1px solid #e2e8f0;
    margin: 18px 0;
  }

  .fa-table-wrap {
    overflow-x: auto;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
  }
  .fa-table {
    width: 100%;
    border-collapse: collapse;
    min-width: 900px;
    font-size: 13px;
  }
  .fa-table th {
    background: #eff6ff;
    color: #1e3a8a;
    padding: 9px 10px;
    text-align: left;
    font-weight: 700;
    font-size: 11px;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    white-space: nowrap;
    border-right: 1px solid #dbeafe;
  }
  .fa-table th:last-child { border-right: none; }
  .fa-table td {
    padding: 7px 8px;
    border-bottom: 1px solid #e2e8f0;
    border-right: 1px solid #f1f5f9;
    vertical-align: middle;
  }
  .fa-table td:last-child { border-right: none; }
  .fa-table tr:hover td { background: #f8fafc; }
  .fa-table tr:last-child td { border-bottom: none; }

  .fa-id-cell {
    font-family: 'IBM Plex Mono', monospace;
    font-size: 11px;
    font-weight: 700;
    color: #2563eb;
    white-space: nowrap;
    min-width: 40px;
  }

  .fa-cell-input, .fa-cell-select {
    border: 1px solid transparent;
    padding: 6px 7px;
    font-family: 'IBM Plex Sans', sans-serif;
    font-size: 13px;
    color: #161616;
    background: transparent;
    width: 100%;
    min-width: 80px;
    outline: none;
    border-radius: 4px;
    transition: border-color 0.15s, background 0.15s;
  }
  .fa-cell-input:focus, .fa-cell-select:focus {
    border-color: #2563eb;
    background: #eff6ff;
  }
  .fa-cell-select { cursor: pointer; }
  .fa-cell-select:disabled { color: #94a3b8; cursor: not-allowed; }

  .fa-th-group {
    background: #dbeafe !important;
    color: #1e3a8a !important;
    text-align: center;
    font-size: 10px;
    letter-spacing: 1px;
    padding: 4px 8px;
    text-transform: uppercase;
    border-right: 1px solid #bfdbfe;
  }

  .fa-btn-add {
    display: inline-flex; align-items: center; gap: 6px;
    margin-top: 10px; padding: 7px 13px;
    background: #fff; border: 1px solid #2563eb; color: #2563eb;
    font-family: 'IBM Plex Sans', sans-serif; font-size: 12px; font-weight: 700;
    cursor: pointer; border-radius: 6px;
    transition: background 0.15s, color 0.15s;
  }
  .fa-btn-add:hover { background: #2563eb; color: #fff; }

  .fa-btn-del {
    background: transparent; border: none; color: #dc2626;
    cursor: pointer; font-size: 16px; line-height: 1;
    padding: 2px 6px; border-radius: 4px; transition: background 0.15s;
  }
  .fa-btn-del:hover { background: #fee2e2; }

  .fa-footer-bar {
    display: flex; justify-content: space-between; align-items: center;
    margin-top: 20px; padding-top: 14px; border-top: 1px solid #e2e8f0;
    gap: 12px;
    flex-wrap: wrap;
  }
  .fa-footer-meta { font-size: 12px; color: #64748b; display: flex; align-items: center; gap: 8px; }

  .fa-btn-prev {
    display: inline-flex; align-items: center; gap: 6px;
    padding: 9px 18px;
    background: #fff; color: #475569;
    border: 1px solid #cbd5e1;
    font-family: 'IBM Plex Sans', sans-serif;
    font-size: 13px; font-weight: 600;
    cursor: pointer; border-radius: 4px;
    transition: background 0.15s;
  }
  .fa-btn-prev:hover:not(:disabled) { background: #f1f5f9; }
  .fa-btn-prev:disabled { opacity: 0.4; cursor: not-allowed; }

  /*  Cancel link in creation mode */
  .fa-btn-cancel {
    background: transparent;
    border: none;
    color: #64748b;
    font-family: 'IBM Plex Sans', sans-serif;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    padding: 9px 12px;
    text-decoration: underline;
    text-underline-offset: 2px;
  }
  .fa-btn-cancel:hover { color: #dc2626; }

  .fa-btn-save {
    display: inline-flex; align-items: center; gap: 8px;
    padding: 10px 24px;
    background: #2563eb; color: #fff;
    border: none;
    font-family: 'IBM Plex Sans', sans-serif;
    font-size: 14px; font-weight: 700;
    cursor: pointer; border-radius: 4px;
    transition: background 0.15s;
  }
  .fa-btn-save:hover:not(:disabled) { background: #1d4ed8; }
  .fa-btn-save:disabled { background: #cbd5e1; cursor: not-allowed; }

  /*  Gradient Save button used ONLY in creation mode */
  .fa-btn-save-primary {
    background: linear-gradient(135deg, #7C6EF2 0%, #4A90E2 100%);
    box-shadow: 0 6px 16px rgba(74, 108, 226, 0.35);
    padding: 11px 28px;
    font-size: 14px;
  }
  .fa-btn-save-primary:hover:not(:disabled) {
    background: linear-gradient(135deg, #6d5fe8 0%, #3d80d0 100%);
    box-shadow: 0 8px 20px rgba(74, 108, 226, 0.45);
    transform: translateY(-1px);
  }
  .fa-btn-save-primary:disabled { background: #cbd5e1; box-shadow: none; }

  .fa-note {
    background: #eff6ff;
    border-left: 3px solid #2563eb;
    padding: 9px 13px;
    font-size: 12px;
    color: #334155;
    margin-bottom: 14px;
    line-height: 1.55;
  }
  
  .fa-warning-note {
  background: #fff8e1;
  border-left: 3px solid #f59e0b;
  padding: 9px 13px;
  font-size: 12px;
  color: #92400e;
  margin-bottom: 14px;
  line-height: 1.55;
  }

  .fa-toast {
    position: fixed; top: 88px; right: 20px; z-index: 1400;
    background: #22c55e; color: #fff;
    padding: 12px 20px; font-size: 13px; font-weight: 600;
    border-radius: 6px;
    display: flex; align-items: center; gap: 8px;
    animation: slideIn 0.2s ease, fadeOut 0.3s ease 2.5s forwards;
    box-shadow: 0 4px 16px rgba(0,0,0,0.15);
  }
  @keyframes slideIn {
    from { transform: translateX(60px); opacity: 0; }
    to { transform: none; opacity: 1; }
  }
  @keyframes fadeOut { to { opacity: 0; transform: translateX(20px); } }
  .fa-toast.error { background: #dc2626; }

  .fa-datepicker-wrap .MuiOutlinedInput-root {
    border-radius: 4px;
    background: #f8fafc;
    font-family: 'IBM Plex Sans', sans-serif;
    font-size: 14px;
  }
  .fa-datepicker-wrap .MuiOutlinedInput-root.Mui-focused fieldset { border-color: #2563eb; }
  .fa-datepicker-wrap .MuiOutlinedInput-notchedOutline { border: 1px solid #cbd5e1; }
`;

// ─── Sub-components ───────────────────────────────────────────────────────────

const Field = ({ label, name, value, onChange, type = 'text', placeholder = '',
                span, help, required, readOnly, autoTag }) => (
  <div className={`fa-field ${span ? 'fa-grid-full' : ''}`}>
    <label className="fa-label">
      {label}
      {required && <span className="fa-req-star">*</span>}
      {autoTag && <span className="fa-auto-tag">{autoTag}</span>}
      {help && (
        <Tooltip title={help} arrow placement="top" enterDelay={150} leaveDelay={100}>
          <span className="fa-label-help">
            <InfoOutlinedIcon sx={{ fontSize: 14 }} />
          </span>
        </Tooltip>
      )}
    </label>
    <input
      className="fa-input"
      type={type}
      name={name}
      value={value || ''}
      onChange={onChange}
      placeholder={placeholder}
      autoComplete="off"
      readOnly={!!readOnly}
    />
  </div>
);

const SelectField = ({ label, name, value, onChange, options, placeholder = 'Select...',
                       span, help, required }) => (
  <div className={`fa-field ${span ? 'fa-grid-full' : ''}`}>
    <label className="fa-label">
      {label}
      {required && <span className="fa-req-star">*</span>}
      {help && (
        <Tooltip title={help} arrow placement="top">
          <span className="fa-label-help">
            <InfoOutlinedIcon sx={{ fontSize: 14 }} />
          </span>
        </Tooltip>
      )}
    </label>
    <select className="fa-select" name={name} value={value || ''} onChange={onChange}>
      <option value="">{placeholder}</option>
      {options.map(o => typeof o === 'string'
        ? <option key={o} value={o}>{o}</option>
        : <option key={o.value} value={o.value}>{o.label}</option>
      )}
    </select>
  </div>
);

const RouteSelect = ({ row, rowIndex, table, field, options, disabled, handleTableCell }) => (
  <td>
    <select
      className="fa-cell-select"
      value={row[field] || ''}
      onChange={e => handleTableCell(table, rowIndex, field, e.target.value)}
      disabled={disabled}
      style={{ minWidth: 130 }}
    >
      <option value="">{disabled ? '-' : 'Select...'}</option>
      {(options || []).map(r => <option key={r} value={r}>{r}</option>)}
    </select>
  </td>
);

const IncludedGoodsSelect = ({ row, rowIndex, field, selectedGoodsCategories, handleTableCell }) => (
  <td>
    <select
      className="fa-cell-select"
      value={row[field] || ''}
      onChange={e => handleTableCell('relevantProcesses', rowIndex, field, e.target.value)}
      disabled={selectedGoodsCategories.length === 0}
      style={{ minWidth: 120 }}
    >
      <option value="">-</option>
      <option value="only direct production">only direct production</option>
      {selectedGoodsCategories.map(c => <option key={c} value={c}>{c}</option>)}
    </select>
  </td>
);

const CNCodeSelect = ({ row, rowIndex, handleTableCell }) => {
  const opts = CN_CODES_BY_GOOD[row.goodsCategory] || [];
  const disabled = !row.goodsCategory || opts.length === 0;
  const selected = opts.find(o => o.code_compact === row.cnCode) || null;

  return (
    <td style={{ minWidth: 280, padding: '4px 6px' }} title={row.cnCode ? getCnLabel(row.cnCode) : ''}>
      <Autocomplete
        size="small"
        disabled={disabled}
        options={opts}
        value={selected}
        getOptionLabel={(o) => o.code || ''}
        isOptionEqualToValue={(o, v) => o.code_compact === v.code_compact}
        onChange={(e, newValue) => {
          handleTableCell('purchasedPrecursors', rowIndex, 'cnCode', newValue ? newValue.code_compact : '');
        }}
        renderOption={(props, option) => (
          <li {...props} key={option.code_compact} style={{ display: 'block', padding: '6px 12px' }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#161616', fontFamily: "'IBM Plex Sans', sans-serif" }}>
              {option.code}
            </div>
            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2, fontFamily: "'IBM Plex Sans', sans-serif" }}>
              {option.description}
            </div>
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={!row.category ? 'Pick goods first' : (opts.length === 0 ? '—' : 'Select CN...')}
            variant="standard"
            InputProps={{
              ...params.InputProps,
              disableUnderline: true,
              style: { fontSize: 13, fontFamily: "'IBM Plex Sans', sans-serif" },
            }}
          />
        )}
      />
    </td>
  );
};
const TabStrip = ({ tabs, active, onSelect, disabledTabs = [] }) => (
  <div className="fa-tabs">
    {tabs.map(t => {
      const isActive = t.id === active;
      const isDisabled = disabledTabs.includes(t.id);
      let badgeClass = 'fa-tab-badge';
      if (t.progress === 100) badgeClass += ' done';
      else if (t.progress >= 41) badgeClass += ' warn';
      else if (t.progress === 0) badgeClass += ' empty';

      const tabInner = (
        <div
          key={t.id}
          className={`fa-tab ${isActive ? 'active' : ''} ${isDisabled ? 'disabled' : ''}`}
          onClick={() => !isDisabled && onSelect(t.id)}
        >
          <span className="fa-tab-num">{t.num}</span>
          <span>{t.title}</span>
          {isDisabled
            ? <span className="fa-tab-lock">🔒</span>
            : <span className={badgeClass}>{t.progress ?? 0}%</span>
          }
        </div>
      );

      return isDisabled ? (
        <Tooltip
          key={t.id}
          title="Available after you save the audit details on tab 01"
          arrow
          placement="top"
        >
          {tabInner}
        </Tooltip>
      ) : tabInner;
    })}
  </div>
);

// ─── Main Component ───────────────────────────────────────────────────────────

const TAB_IDS = ['t1', 't2', 't3', 't4', 't5'];
const CREATION_SENTINEL = 'new';

//  Setup-tab required fields (validated in creation mode)
const SETUP_REQUIRED_FIELDS = [
  { key: 'auditName',            label: 'Report name',           check: (fd) => !!fd.auditName?.trim() },
  { key: 'auditDescription',     label: 'Report description',    check: (fd) => !!fd.auditDescription?.trim() },
  { key: 'reportingFrequency',   label: 'Reporting frequency',  check: (fd) => !!fd.reportingFrequency },
  { key: 'reportingPeriodStart', label: 'Start date',           check: (fd) => !!fd.reportingPeriodStart },
  { key: 'reportingPeriodEnd',   label: 'End date',             check: (fd) => !!fd.reportingPeriodEnd },
  { key: 'sector',               label: 'CBAM sector',          check: (fd) => !!fd.sector },
];

const FormA = () => {
  const navigate = useNavigate();
  const { auditId: urlAuditId } = useParams();
  const { formsData } = useAuditProgress(urlAuditId);
  const { updateForm } = useFormContext();
  const { currentUser, tenantId, tenantName } = useAuth();

  //  Detect creation mode from URL sentinel
  const isCreationMode = !urlAuditId || urlAuditId === CREATION_SENTINEL;

  const [formData, setFormData] = useState(() => ({
    ...DEFAULT_STATE,
    aggregatedGoods: [newGoodsRow(0)],
    relevantProcesses: [newProcessRow(0)],
    purchasedPrecursors: [newPrecursorRow(0)],
  }));

  const sectionProgress = {
    reportingPeriod: calculateRequiredProgress(formData, FORM_A_REQUIRED_FIELDS.reportingPeriod),
    installation: calculateRequiredProgress(formData, FORM_A_REQUIRED_FIELDS.installation),
    verifier: calculateRequiredProgress(formData, FORM_A_REQUIRED_FIELDS.verifier),
    aggregatedGoods: calculateRequiredProgressForRows(
      formData.aggregatedGoods || [], FORM_A_REQUIRED_FIELDS.aggregatedGoods
    ),
    relevantProcesses: calculateRequiredProgressForRows(
      formData.relevantProcesses || [], FORM_A_REQUIRED_FIELDS.relevantProcesses
    ),
    purchasedPrecursors: calculateRequiredProgressForRows(
      formData.purchasedPrecursors || [], FORM_A_REQUIRED_FIELDS.purchasedPrecursors
    ),
  };

  //  Setup tab progress — inline calc from SETUP_REQUIRED_FIELDS
  const setupProgress = Math.round(
    (SETUP_REQUIRED_FIELDS.filter(f => f.check(formData)).length /
      SETUP_REQUIRED_FIELDS.length) * 100
  );

  const tab1Progress = Math.round(
    (sectionProgress.reportingPeriod.progress + sectionProgress.installation.progress) / 2
  );
  const tab3Progress = Math.round(
    (sectionProgress.aggregatedGoods.progress + sectionProgress.relevantProcesses.progress) / 2
  );

  const [activeTab, setActiveTab] = useState('t1');
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [periodError, setPeriodError] = useState('');

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 2800);
  };

  //  Form A subcollection sync — only when editing an existing audit
  useEffect(() => {
    if (isCreationMode) return;

    const emptyFormA = {
      ...DEFAULT_STATE,
      aggregatedGoods: [newGoodsRow(0)],
      relevantProcesses: [newProcessRow(0)],
      purchasedPrecursors: [newPrecursorRow(0)],
    };

    const ref = doc(db, "auditReports", urlAuditId, "form_a", "main");

    const unsub = onSnapshot(ref, (snap) => {
      if (snap.exists()) {
        const loaded = fromFirestore(snap.data(), emptyFormA);
        //  Preserve audit-level metadata (loaded from parent doc, not form_a)
        setFormData(prev => ({
          ...loaded,
          auditName: prev.auditName || '',
          auditDescription: prev.auditDescription || '',
          reportingFrequency: prev.reportingFrequency || '',
          reportingQuarter: prev.reportingQuarter || '',
          reportingYear: prev.reportingYear || dayjs().year(),
        }));
        updateForm("formA", loaded);
      } else {
        setFormData(prev => ({
          ...emptyFormA,
         auditName: prev.auditName || '',
          auditDescription: prev.auditDescription || '',
          reportingFrequency: prev.reportingFrequency || '',
          reportingQuarter: prev.reportingQuarter || '',
          reportingYear: prev.reportingYear || dayjs().year(),
        }));
        updateForm("formA", {});
      }
    });

    return () => unsub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlAuditId, isCreationMode]);

  //  Parent audit doc: one-time load of name / description / frequency
  useEffect(() => {
    if (isCreationMode || !urlAuditId) return;
    let cancelled = false;
    (async () => {
      try {
        const ref = doc(db, 'auditReports', urlAuditId);
        const snap = await getDoc(ref);
        if (cancelled || !snap.exists()) return;
        const data = snap.data();
        setFormData(prev => ({
          ...prev,
          auditName: data.name || '',
          auditDescription: data.description || '',
          reportingFrequency: data.reportingFrequency || '',
          reportingQuarter: data.reportingQuarter || '',
          reportingYear: data.reportingYear || dayjs().year(),
        }));
        // If this audit was created by "Duplicate with Defaults",
        // we previously auto-advanced to tab 2. Keep the user on tab 1
        // (Setup) so they start by reviewing audit metadata.
      } catch (err) {
        console.error('Error loading audit metadata:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [urlAuditId, isCreationMode]);

  // ── Handlers ──────────────────────────────────────────────────────────────

  const set = useCallback((patch) => {
    setFormData(prev => {
      const next = typeof patch === 'function' ? patch(prev) : { ...prev, ...patch };
      updateForm('formA', next);
      return next;
    });
  }, [updateForm]);

const handleInput = useCallback((e) => {
    set({ [e.target.name]: e.target.value });
  }, [set]);

  //  Frequency change: Annual auto-fills full-year dates; leaving Annual clears them
  const handleFrequencyChange = useCallback((e) => {
    const newFrequency = e.target.value;
    setPeriodError('');

    if (newFrequency === 'Annual') {
      const { start, end } = getDefaultAnnualPeriod(formData.reportingYear || dayjs().year());
      set({
        reportingFrequency: newFrequency,
        reportingQuarter: '',
        reportingPeriodStart: start,
        reportingPeriodEnd: end,
      });
    } else {
      set({
        reportingFrequency: newFrequency,
        reportingQuarter: '',
        reportingPeriodStart: null,
        reportingPeriodEnd: null,
      });
    }
  }, [set, formData.reportingYear]);

  //  Quarter change: auto-fills locked, leap-year-aware quarter dates
  const handleQuarterChange = useCallback((e) => {
    const newQuarter = e.target.value;
    setPeriodError('');

    if (!newQuarter) {
      set({ reportingQuarter: '', reportingPeriodStart: null, reportingPeriodEnd: null });
      return;
    }
    const year = formData.reportingYear || dayjs().year();
    const { start, end } = getQuarterPeriod(newQuarter, year);
    set({ reportingQuarter: newQuarter, reportingPeriodStart: start, reportingPeriodEnd: end });
  }, [set, formData.reportingYear]);

  const handleReportingYearChange = useCallback((e) => {
    const year = Number(e.target.value) || dayjs().year();
    set(prev => {
      const next = { ...prev, reportingYear: year };
      if (prev.reportingFrequency === 'Annual') {
        const { start, end } = getDefaultAnnualPeriod(year);
        next.reportingPeriodStart = start;
        next.reportingPeriodEnd = end;
      } else if (prev.reportingFrequency === 'Quarterly' && prev.reportingQuarter) {
        const { start, end } = getQuarterPeriod(prev.reportingQuarter, year);
        next.reportingPeriodStart = start;
        next.reportingPeriodEnd = end;
      }
      return next;
    });
  }, [set]);

  const handleUnlocode = useCallback((e) => {
    const raw = e.target.value || '';
    const code = raw.toUpperCase().trim();
    set(prev => {
      const next = { ...prev, unLocode: raw };
      const hit = code.length === 5 ? UNLOCODE_DB.codes?.[code] : null;
      if (hit) {
        next.latitude  = String(hit.latitude);
        next.longitude = String(hit.longitude);
      }
      return next;
    });
  }, [set]);

  //  Validate reporting period whenever dates/frequency change
  useEffect(() => {
    if (formData.reportingPeriodStart && formData.reportingPeriodEnd) {
      const result = validateReportingPeriod(
        formData.reportingPeriodStart,
        formData.reportingPeriodEnd,
        formData.reportingFrequency
      );
      setPeriodError(result.valid ? '' : result.message);
    } else {
      setPeriodError('');
    }
  }, [formData.reportingPeriodStart, formData.reportingPeriodEnd, formData.reportingFrequency]);

  const handleTableCell = useCallback((table, rowIndex, field, value) => {
    set(prev => {
      const rows = [...(prev[table] || [])];
      const row = { ...rows[rowIndex], [field]: value };
      if (table === 'aggregatedGoods' && field === 'category') {
        row.cnCode = '';
        row.primaryRoute = '';
        row.route1 = ''; row.route2 = ''; row.route3 = '';
        row.route4 = ''; row.route5 = ''; row.route6 = '';
      }
      if (table === 'relevantProcesses' && field === 'aggregatedGoodsCategory') {
        row.inc1 = ''; row.inc2 = ''; row.inc3 = '';
        row.inc4 = ''; row.inc5 = ''; row.inc6 = '';
      }
      rows[rowIndex] = row;
      return { ...prev, [table]: rows };
    });
  }, [set]);

  const addRow = useCallback((table, factory) => {
    set(prev => {
      const rows = [...(prev[table] || [])];
      rows.push(factory(rows.length));
      return { ...prev, [table]: rows };
    });
  }, [set]);

  const delRow = useCallback((table, index) => {
    set(prev => {
      const filtered = (prev[table] || []).filter((_, i) => i !== index);
      const prefixMap = { aggregatedGoods: 'G', relevantProcesses: 'P', purchasedPrecursors: 'PP' };
      const prefix = prefixMap[table] || 'R';
      const rows = filtered.map((r, i) => ({ ...r, id: `${prefix}${i + 1}` }));
      return { ...prev, [table]: rows };
    });
  }, [set]);

  // ── Save logic ─────────────────────────────────────────────────────────────

  const calculateFormAProgress = (fd) => {
    const reportingPeriod = calculateRequiredProgress(fd, FORM_A_REQUIRED_FIELDS.reportingPeriod);
    const installation = calculateRequiredProgress(fd, FORM_A_REQUIRED_FIELDS.installation);
    const verifier = calculateRequiredProgress(fd, FORM_A_REQUIRED_FIELDS.verifier);
    const aggregatedGoods = calculateRequiredProgressForRows(fd.aggregatedGoods || [], FORM_A_REQUIRED_FIELDS.aggregatedGoods);
    const relevantProcesses = calculateRequiredProgressForRows(fd.relevantProcesses || [], FORM_A_REQUIRED_FIELDS.relevantProcesses);
    const purchasedPrecursors = calculateRequiredProgressForRows(fd.purchasedPrecursors || [], FORM_A_REQUIRED_FIELDS.purchasedPrecursors);

    const editableTotal =
      reportingPeriod.total + installation.total + verifier.total +
      aggregatedGoods.total + relevantProcesses.total + purchasedPrecursors.total;

    const editableFilled =
      reportingPeriod.filled + installation.filled + verifier.filled +
      aggregatedGoods.filled + relevantProcesses.filled + purchasedPrecursors.filled;

    const progress = editableTotal === 0
      ? 0
      : Math.round((editableFilled / editableTotal) * 100);

    return {
      editableTotal, editableFilled, progress,
      status: progress >= 100 ? "Completed" : progress > 0 ? "In Progress" : "Not Started",
      sections: {
        reportingPeriod:     { title: "Reporting Period",              ...reportingPeriod },
        installation:        { title: "About the Manufacturing Facility",        ...installation },
        verifier:            { title: "Verifier",                      ...verifier },
        aggregatedGoods:     { title: "Aggregated Goods",              ...aggregatedGoods },
        relevantProcesses:   { title: "Relevant Production Processes", ...relevantProcesses },
        purchasedPrecursors: { title: "Purchased Precursors",          ...purchasedPrecursors },
      },
    };
  };

  //  Validation for creation-mode save — uses SETUP_REQUIRED_FIELDS
  const validateCreationRequired = () => {
    const missing = SETUP_REQUIRED_FIELDS
      .filter(f => !f.check(formData))
      .map(f => f.label);

    if (missing.length === 0 && formData.reportingPeriodStart && formData.reportingPeriodEnd) {
      const periodResult = validateReportingPeriod(
        formData.reportingPeriodStart,
        formData.reportingPeriodEnd,
        formData.reportingFrequency
      );
      if (!periodResult.valid) missing.push(periodResult.message);
    }
    return missing;
  };

  // Core save — writes to Firestore, returns the resolved auditId
  const persist = async () => {
    //  In creation mode, generate the ID here; in edit mode, use URL param
    const auditId = isCreationMode ? crypto.randomUUID() : urlAuditId;
    console.log("SAVING precursors:", formData.purchasedPrecursors);
    const ref = doc(db, 'auditReports', auditId, 'form_a', 'main');
    const auditRef = doc(db, 'auditReports', auditId);
    const userEmail = currentUser?.email || '';

    const progressMeta = calculateFormAProgress(formData);

    // Form A subcollection payload
    const payload = {
      ...toFirestore(formData),
      progressMeta,
      updatedAt: new Date(),
      meta: {
        updatedAt: new Date(),
        ...(isCreationMode && { createdAt: new Date() }),
      },
    };
    await setDoc(ref, payload, { merge: true });

    // Parent audit doc payload
    // Always sync audit-level metadata (users can edit name/desc/freq later too)
    const parentPatch = {
      name: formData.auditName?.trim() || '',
      description: formData.auditDescription?.trim() || '',
      reportingFrequency: formData.reportingFrequency || '',
      reportingQuarter: formData.reportingQuarter || '',
      reportingYear: formData.reportingYear || dayjs().year(),
      updatedBy: userEmail,
      updatedAt: serverTimestamp(),
    };

    if (isCreationMode) {
      parentPatch.createdBy = userEmail;
      parentPatch.createdAt = serverTimestamp();
      parentPatch.status = 'draft';
      if (tenantId)   parentPatch.tenantId   = tenantId;
      if (tenantName) parentPatch.tenantName = tenantName;
    }

    await setDoc(auditRef, parentPatch, { merge: true });

    updateForm('formA', formData);
    return auditId;
  };

  //  Unified save handler — behaves differently based on mode + tab
  const handleSave = async () => {
    //  Creation mode: validate first
    if (isCreationMode) {
      const missing = validateCreationRequired();
      if (missing.length > 0) {
        showToast(`Please fill: ${missing.join(', ')}`, 'error');
        return;
      }
    }

    setSaving(true);
    try {
      const auditId = await persist();

      if (isCreationMode) {
        //  Stay in Form A after creation — advance to Installation tab
        //  BEFORE navigating. Same-route param change (/form-a/new →
        //  /form-a/{id}) doesn't remount, so setActiveTab persists.
        showToast('Report created — fill in the manufacturing facility next');
        setActiveTab('t2');
        setTimeout(() => {
          navigate(`/form-a/${auditId}`, { replace: true });
        }, 400);
        return;
      }

      //  Edit mode — behave like before
      if (activeTab === 't5') {
        showToast('Form A saved successfully');
        setTimeout(() => navigate(`/form-b/${auditId}`), 600);
      } else {
        const i = TAB_IDS.indexOf(activeTab);
        if (i >= 0 && i < TAB_IDS.length - 1) {
          setActiveTab(TAB_IDS[i + 1]);
          window.scrollTo({ top: 0, behavior: 'smooth' });
          showToast('Saved');
        }
      }
    } catch (err) {
      console.error(err);
      showToast('Save failed — check console', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handlePrevTab = () => {
    const i = TAB_IDS.indexOf(activeTab);
    if (i > 0) {
      setActiveTab(TAB_IDS[i - 1]);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  //  Cancel — creation mode only. Nothing was written, just navigate back.
  const handleCancel = () => {
    navigate('/audit-dashboard');
  };

  // ── Derived ────────────────────────────────────────────────────────────────

  const selectedGoodsCategories = useMemo(() =>
    [...new Set((formData.aggregatedGoods || []).map(r => r.category).filter(Boolean))],
    [formData.aggregatedGoods]
  );

  const datePickerSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: 1,
      backgroundColor: '#f8fafc',
      fontFamily: "'IBM Plex Sans', sans-serif",
      fontSize: 14,
      '&.Mui-focused fieldset': { borderColor: '#2563eb' },
      '& fieldset': { borderColor: '#cbd5e1' },
    },
  };

  const tabs = [
    { id: 't1', num: '01', title: 'Setup',                 progress: setupProgress },
    { id: 't2', num: '02', title: 'Manufacturing Facility',          progress: sectionProgress.installation.progress },
    { id: 't3', num: '03', title: 'Verifier',              progress: sectionProgress.verifier.progress },
    { id: 't4', num: '04', title: 'Goods & Processes',     progress: tab3Progress },
    { id: 't5', num: '05', title: 'Purchased Precursors',  progress: sectionProgress.purchasedPrecursors.progress },
  ];

  //  In creation mode, force tab 1 & disable tabs 2-5
  const effectiveActiveTab = isCreationMode ? 't1' : activeTab;
  const disabledTabs = isCreationMode ? ['t2', 't3', 't4', 't5'] : [];

  const isLastTab = effectiveActiveTab === 't5';
  //  Save-button label depends on mode
  const saveLabel = isCreationMode
    ? (saving ? 'Creating…' : 'Save & Continue →')
    : (saving ? 'Saving…' : (isLastTab ? 'Save & Continue →' : 'Next →'));

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <>
      <style>{STYLE}</style>

      <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "#f8fafc" }}>
        {/*  Sidebar hidden in creation mode */}
        {!isCreationMode && <AuditSidebar displayAuditId={urlAuditId} formsData={formsData} />}

        <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <div className="fa-root">

              <div className="fa-header-slim">
                <div>
                  <div className="sub"></div>
                  <div className="title">
                    {isCreationMode
                      ? 'New Report'
                      : (tabs.find(t => t.id === effectiveActiveTab)?.title || 'Form A')}
                  </div>
                </div>
              </div>

              <div className="fa-body">

                {/*  Creation-mode banner */}
                {isCreationMode && (
                  <div className="fa-creation-banner">
                    <div className="icon">✨</div>
                    <div className="body">
                      <div className="title-line">You're starting a new audit</div>
                      <div className="sub-line">
                        Fill in the audit name, reporting period and manufacturing facility details below,
                        then click <strong>Save &amp; Continue</strong> to create it. Nothing is
                        saved until you do.
                      </div>
                    </div>
                  </div>
                )}

                <TabStrip
                  tabs={tabs}
                  active={effectiveActiveTab}
                  onSelect={setActiveTab}
                  disabledTabs={disabledTabs}
                />

                {/* ╔══════════════ TAB 1: SETUP (creation gate) ══════════════╗ */}
                {effectiveActiveTab === 't1' && (
                  <div className="fa-tab-panel">

                    {/* Report identification: name + description */}
                    <div className="fa-subsection">
                      <span className="fa-sub-label">Report identification</span>

                      <div className="fa-grid">
                        <div className="fa-field fa-grid-full">
                          <label className="fa-label">
                            Report name <span className="fa-req-star">*</span>
                          </label>
                          <input
                            className="fa-input"
                            type="text"
                            name="auditName"
                            value={formData.auditName || ''}
                            onChange={handleInput}
                            placeholder="e.g. Steel Q1 2026, Cement plant – Turkey"
                            maxLength={100}
                            autoComplete="off"
                          />
                          <div className="fa-charcount">
                            {(formData.auditName || '').length}/100
                          </div>
                        </div>

                        <div className="fa-field fa-grid-full">
                          <label className="fa-label">Description</label>
                          <textarea
                            className="fa-textarea"
                            name="auditDescription"
                            value={formData.auditDescription || ''}
                            onChange={handleInput}
                            placeholder="Brief notes about this audit, scope, reporting period, etc. (optional)"
                            maxLength={500}
                            rows={3}
                          />
                          <div className="fa-charcount">
                            {(formData.auditDescription || '').length}/500
                          </div>
                        </div>
                      </div>
                    </div>

                    <hr className="fa-divider" />

                    {/* Reporting period: frequency + start + end */}
                    <div className="fa-subsection">
                      <span className="fa-sub-label">Reporting period</span>
                      <div className="fa-note">
                          The reporting period refers to the timeframe during which all relevant data, such as production quantities, electricity consumption, and other operational information, is collected for reporting purposes. The recommended reporting period is from 1 January to 31 December of a calendar year. However, in cases where data availability is limited, the reporting period must cover a minimum duration of three months to ensure adequate and representative data for reporting.

                      </div>
                      <div className="fa-date-row">
                        <div className="fa-field">
                          <label className="fa-label">
                            Reporting frequency <span className="fa-req-star">*</span>
                            <Tooltip title={HELP_FREQUENCY} arrow placement="top">
                              <span className="fa-label-help">
                                <InfoOutlinedIcon sx={{ fontSize: 14 }} />
                              </span>
                            </Tooltip>
                          </label>
                          <select
                            className="fa-select"
                            name="reportingFrequency"
                            value={formData.reportingFrequency || ''}
                            onChange={handleFrequencyChange}
                          >
                            <option value="">Select...</option>
                            <option value="Annual">Annual</option>
                            <option value="Quarterly">Quarterly</option>
                          </select>
                        </div>

                        <div className="fa-field">
                          <label className="fa-label">Reporting year</label>
                          <select
                            className="fa-select"
                            value={formData.reportingYear || dayjs().year()}
                            onChange={handleReportingYearChange}
                          >
                            {Array.from({ length: 6 }, (_, i) => dayjs().year() - 2 + i).map(y => (
                              <option key={y} value={y}>
                                {y}{isLeapYear(y) ? ' (leap year)' : ''}
                              </option>
                            ))}
                          </select>
                        </div>

                        {formData.reportingFrequency === 'Quarterly' && (
                          <div className="fa-field">
                            <label className="fa-label">
                              Quarter <span className="fa-req-star">*</span>
                            </label>
                            <select
                              className="fa-select"
                              value={formData.reportingQuarter || ''}
                              onChange={handleQuarterChange}
                            >
                              <option value="">Select quarter...</option>
                              <option value="Q1">Q1 (Jan – Mar)</option>
                              <option value="Q2">Q2 (Apr – Jun)</option>
                              <option value="Q3">Q3 (Jul – Sep)</option>
                              <option value="Q4">Q4 (Oct – Dec)</option>
                            </select>
                          </div>
                        )}
                      </div>

                      <div className="fa-date-row-2col" style={{ marginTop: 14 }}>
                        <div className="fa-field">
                          <label className="fa-label">
                            Start date <span className="fa-req-star">*</span>
                          </label>
                          <DatePicker
                            value={formData.reportingPeriodStart || null}
                            onChange={(val) => set({ reportingPeriodStart: val })}
                            format="DD/MM/YYYY"
                            views={['year', 'month', 'day']}
                            openTo="year"
                            disabled={formData.reportingFrequency === 'Quarterly'}
                            slotProps={{
                              textField: { size: 'small', fullWidth: true,
                                placeholder: 'DD/MM/YYYY', sx: datePickerSx },
                            }}
                          />
                        </div>
                        <div className="fa-field">
                          <label className="fa-label">
                            End date <span className="fa-req-star">*</span>
                          </label>
                          <DatePicker
                            value={formData.reportingPeriodEnd || null}
                            onChange={(val) => set({ reportingPeriodEnd: val })}
                            format="DD/MM/YYYY"
                            views={['year', 'month', 'day']}
                            openTo="year"
                            minDate={formData.reportingPeriodStart || undefined}
                            disabled={formData.reportingFrequency === 'Quarterly'}
                            slotProps={{
                              textField: { size: 'small', fullWidth: true,
                                placeholder: 'DD/MM/YYYY', sx: datePickerSx },
                            }}
                          />
                        </div>
                      </div>

                      {periodError && (
                        <div style={{ color: '#dc2626', fontSize: 12, marginTop: 8, fontWeight: 600 }}>
                          {periodError}
                        </div>
                      )}
                    </div>

                    <hr className="fa-divider" />

                    {/* CBAM sector classification */}
                    <div className="fa-subsection">
                      <span className="fa-sub-label">CBAM sector classification</span>
                      <div className="fa-note">
                        Select the CBAM sector this audit covers. This defines the goods
                        categories and production routes available in later tabs.
                      </div>
                      <div className="fa-grid">
                        <SelectField
                          label="CBAM sector"
                          name="sector"
                          value={formData.sector}
                          onChange={handleInput}
                          options={CBAM_SECTORS}
                          placeholder="Select sector..."
                          required
                          span
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* ╔══════════════ TAB 2: INSTALLATION ══════════════╗ */}
                {!isCreationMode && effectiveActiveTab === 't2' && (
                  <div className="fa-tab-panel">

                    {/* Organisation type */}
                    <div className="fa-subsection">
                      <span className="fa-sub-label">Organisation type</span>
                      <div className="fa-orgtype-box">
                        <FormControl>
                          <RadioGroup
                            row
                            name="organisationType"
                            value={formData.organisationType || 'Supplier'}
                            onChange={(e) => set({ organisationType: e.target.value })}
                          >
                            <FormControlLabel
                              value="Supplier"
                              control={<Radio size="small" sx={{ color: '#2563eb', '&.Mui-checked': { color: '#2563eb' } }} />}
                              label="Supplier"
                              sx={{ mr: 4, '& .MuiFormControlLabel-label': { fontSize: 14 } }}
                            />
                            <FormControlLabel
                              value="Importer"
                              control={<Radio size="small" sx={{ color: '#2563eb', '&.Mui-checked': { color: '#2563eb' } }} />}
                              label="Importer"
                              sx={{ '& .MuiFormControlLabel-label': { fontSize: 14 } }}
                            />
                          </RadioGroup>
                        </FormControl>
                      </div>
                    </div>

                    <hr className="fa-divider" />

                    {/* About the installation (sector moved to Setup tab) */}
                    <div className="fa-subsection">
                      <span className="fa-sub-label">Manufacturing Facility</span>

                      <div className="fa-grid">
                        <Field
                          label="Name of manufacturing facility"
                          name="installationName"
                          value={formData.installationName}
                          onChange={handleInput}
                          required
                          span
                        />

                        <Field
                          label="Economic activity"
                          name="economicActivity"
                          value={formData.economicActivity}
                          onChange={handleInput}
                          help={HELP_ECONOMIC_ACTIVITY}
                        />

                        <Field label="Street, Number" name="street" value={formData.street}
                          onChange={handleInput} />

                        <Field label="P.O. Box"      name="poBox"  value={formData.poBox}
                          onChange={handleInput} />
                        <Field label="Post code" name="postCode" value={formData.postCode}
                          onChange={handleInput} required />

                        <Field label="City"      name="city"     value={formData.city}
                          onChange={handleInput} required />

                        <div className="fa-field">
                          <label className="fa-label">
                            Country <span className="fa-req-star">*</span>
                          </label>
                          <CountryAutocomplete
                            value={formData.country || ''}
                            onChange={(code) => set({ country: code })}
                            placeholder="Search country..."
                            fullWidth
                          />
                        </div>

                        <Field
                          label="UNLOCODE"
                          name="unLocode"
                          value={formData.unLocode}
                          onChange={handleUnlocode}
                          help={HELP_UNLOCODE}
                          placeholder="e.g. INBOM"
                        />

                        <Field
                          label="Latitude (main emission source)"
                          name="latitude"
                          value={formData.latitude}
                          onChange={handleInput}
                          readOnly={!!(formData.unLocode && UNLOCODE_DB.codes?.[(formData.unLocode || '').toUpperCase().trim()])}
                          autoTag={
                            formData.unLocode && UNLOCODE_DB.codes?.[(formData.unLocode || '').toUpperCase().trim()]
                              ? ''
                              : null
                          }
                        />
                        <Field
                          label="Longitude (main emission source)"
                          name="longitude"
                          value={formData.longitude}
                          onChange={handleInput}
                          readOnly={!!(formData.unLocode && UNLOCODE_DB.codes?.[(formData.unLocode || '').toUpperCase().trim()])}
                          autoTag={
                            formData.unLocode && UNLOCODE_DB.codes?.[(formData.unLocode || '').toUpperCase().trim()]
                              ? ''
                              : null
                          }
                        />

                        <Field
                          label="Name of authorised representative"
                          name="nameAuthorizedRepresentative"
                          value={formData.nameAuthorizedRepresentative}
                          onChange={handleInput}
                          span
                        />

                        <Field label="Email" name="email" type="email" value={formData.email}
                          onChange={handleInput} required />

                        <div className="fa-field">
                          <label className="fa-label">
                            Telephone <span className="fa-req-star">*</span>
                          </label>
                          <div className="fa-phone-row">
                            <select
                              className="fa-select"
                              name="phoneCountryCode"
                              value={formData.phoneCountryCode || '+91'}
                              onChange={handleInput}
                            >
                              {PHONE_CODES.map(p => (
                                <option key={`${p.iso}-${p.code}`} value={p.code}>
                                  {p.code} ({p.iso})
                                </option>
                              ))}
                            </select>
                            <input
                              className="fa-input"
                              type="tel"
                              name="telephone"
                              value={formData.telephone || ''}
                              onChange={handleInput}
                              placeholder="Phone number"
                              autoComplete="off"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* ╔══════════════ TAB 3: Verifier (edit-mode only) ══════════════╗ */}
                {!isCreationMode && effectiveActiveTab === 't3' && (
                  <div className="fa-tab-panel">
                    <div className="fa-note">
                      Verifier information is <strong>optional</strong> and not required during the
                      CBAM transitional period. You can come back and fill this in later.
                    </div>

                    <div className="fa-subsection">
                      <span className="fa-sub-label">(a) Name and address of the verifier</span>
                      <div className="fa-grid">
                        <Field label="Company name"    name="verifierCompanyName" value={formData.verifierCompanyName} onChange={handleInput} />
                        <Field label="Street, Number" name="verifierStreet"      value={formData.verifierStreet}      onChange={handleInput} />
                        <Field label="City"          name="verifierCity"        value={formData.verifierCity}        onChange={handleInput} />
                        <Field label="Postcode/ZIP"   name="verifierPostcode"    value={formData.verifierPostcode}    onChange={handleInput} />
                        <Field label="Country"         name="verifierCountry"     value={formData.verifierCountry}     onChange={handleInput} />
                      </div>
                    </div>

                    <div className="fa-subsection">
                      <span className="fa-sub-label">(b) Authorised representative of the verifier</span>
                      <div className="fa-grid">
                        <Field label="Name"           name="verifierRepresentativeName" value={formData.verifierRepresentativeName} onChange={handleInput} />
                        <Field label="Email address" name="verifierEmail"    type="email" value={formData.verifierEmail}    onChange={handleInput} />
                        <Field label="Telephone"    name="verifierTelephone" type="tel"  value={formData.verifierTelephone} onChange={handleInput} />
                        <Field label="Fax"           name="verifierFax"                   value={formData.verifierFax}       onChange={handleInput} />
                      </div>
                    </div>

                    <div className="fa-subsection">
                      <span className="fa-sub-label">(c) Verifier accreditation information</span>
                      <div className="fa-grid">
                        <Field label="Accreditation Member State"                       name="verifierAccreditationMemberState"        value={formData.verifierAccreditationMemberState}       onChange={handleInput} />
                        <Field label="Name of national accreditation body"             name="verifierAccreditationNationalAuthority"  value={formData.verifierAccreditationNationalAuthority} onChange={handleInput} />
                        <Field label="Registration number issued by accreditation body" name="verifierRegistrationNumber"            value={formData.verifierRegistrationNumber}             onChange={handleInput} span />
                      </div>
                    </div>
                  </div>
                )}

                {/* ╔══════════════ TAB 4: Goods & Processes (edit-mode only) ══════════════╗ */}
                {!isCreationMode && effectiveActiveTab === 't4' && (
                  <div className="fa-tab-panel">

                    <div className="fa-subsection">
                      <span className="fa-sub-label">
                        (a) List of aggregated goods categories, relevant precursors and corresponding production routes
                      </span>
                      <div className="fa-note">
                        List ALL aggregated goods categories, including any relevant precursor types produced WITHIN
                        the installation. Where relevant, list all production routes through which the aggregated
                        goods are produced.
                      </div>
                      <div className="fa-table-wrap">
                        <table className="fa-table">
                          <thead>
                            <tr>
                              <th rowSpan={2} style={{ verticalAlign: 'bottom' }}>ID</th>
                              <th rowSpan={2} style={{ verticalAlign: 'bottom', minWidth: 160 }}>Aggregated goods category</th>
                              <th rowSpan={2} style={{ verticalAlign: 'bottom', minWidth: 140 }}>Route (primary)</th>
                              <th colSpan={6} className="fa-th-group">Additional production routes</th>
                              <th rowSpan={2} style={{ verticalAlign: 'bottom' }}>Actions</th>
                            </tr>
                            <tr>
                              {['Route 1','Route 2','Route 3','Route 4','Route 5','Route 6'].map(r => (
                                <th key={r} style={{ background: '#4d5358', color: '#fff', fontSize: 10, minWidth: 110 }}>{r}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {(formData.aggregatedGoods || []).map((row, i) => {
                              const routes = ROUTES_BY_CATEGORY[row.category] || [];
                              const disabled = !row.category;
                              return (
                                <tr key={row._uuid || i}>
                                  <td className="fa-id-cell">{row.id}</td>
                                  <td>
                                    <select
                                      className="fa-cell-select"
                                      value={row.category || ''}
                                      onChange={e => handleTableCell('aggregatedGoods', i, 'category', e.target.value)}
                                      style={{ minWidth: 160 }}
                                    >
                                      <option value="">Select product...</option>
                                      {GOODS_NAMES.map(g => <option key={g} value={g}>{g}</option>)}
                                    </select>
                                  </td>
                                  <RouteSelect
                                    row={row} rowIndex={i} table="aggregatedGoods"
                                    field="primaryRoute" options={routes} disabled={disabled}
                                    handleTableCell={handleTableCell}
                                  />
                                  {['route1','route2','route3','route4','route5','route6'].map(f => (
                                    <RouteSelect
                                      key={f} row={row} rowIndex={i} table="aggregatedGoods"
                                      field={f} options={routes} disabled={disabled}
                                      handleTableCell={handleTableCell}
                                    />
                                  ))}
                                  <td>
                                    <button className="fa-btn-del" onClick={() => delRow('aggregatedGoods', i)}>×</button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                      <button className="fa-btn-add" onClick={() => addRow('aggregatedGoods', newGoodsRow)}>
                        + Add goods row
                      </button>
                    </div>

                    <hr className="fa-divider" />

                    <div className="fa-subsection">
                      <span className="fa-sub-label">(b) Relevant production processes</span>
                      <div className="fa-note">
                        For each aggregated goods category above, define distinct production processes.
                        <strong> Give each process a clear name</strong> — this name will appear in Form D and on
                        the final summary, so it should be meaningful (e.g. "Blast furnace line 1", "EAF unit").
                        Each process covers one goods category and a set of included goods.
                        Where only one goods category is directly produced, select "only direct production" in column 1.
                      </div>
                      <div className="fa-table-wrap">
                        <table className="fa-table">
                          <thead>
                            <tr>
                              <th rowSpan={2} style={{ verticalAlign: 'bottom' }}>ID</th>
                              <th rowSpan={2} style={{ verticalAlign: 'bottom', minWidth: 140 }}>Aggregated goods category</th>
                              <th rowSpan={2} style={{ verticalAlign: 'bottom', minWidth: 170 }}>Process Name</th>
                              <th colSpan={6} className="fa-th-group">Included goods categories listed under (a)</th>
                              <th rowSpan={2} style={{ verticalAlign: 'bottom', minWidth: 110 }}>Error message</th>
                              <th rowSpan={2} style={{ verticalAlign: 'bottom' }}>Actions</th>
                            </tr>
                            <tr>
                              {[1,2,3,4,5,6].map(n => (
                                <th key={n} style={{ background: '#4d5358', color: '#fff', fontSize: 10, minWidth: 110 }}>{n}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {(formData.relevantProcesses || []).map((row, i) => (
                              <tr key={row._uuid || i}>
                                <td className="fa-id-cell">{row.id}</td>
                                <td>
                                  <select
                                    className="fa-cell-select"
                                    value={row.aggregatedGoodsCategory || ''}
                                    onChange={e => handleTableCell('relevantProcesses', i, 'aggregatedGoodsCategory', e.target.value)}
                                    disabled={selectedGoodsCategories.length === 0}
                                    style={{ minWidth: 140 }}
                                  >
                                    <option value="">
                                      {selectedGoodsCategories.length === 0 ? 'Add goods above first' : 'Select...'}
                                    </option>
                                    {selectedGoodsCategories.map(c => <option key={c} value={c}>{c}</option>)}
                                  </select>
                                </td>
                                <td>
                                  <input
                                    className="fa-cell-input"
                                    value={row.name || ''}
                                    onChange={e => handleTableCell('relevantProcesses', i, 'name', e.target.value)}
                                    placeholder="Process name..."
                                    style={{ minWidth: 170 }}
                                  />
                                </td>
                                {['inc1','inc2','inc3','inc4','inc5','inc6'].map(f => (
                                  <IncludedGoodsSelect
                                    key={f} row={row} rowIndex={i} field={f}
                                    selectedGoodsCategories={selectedGoodsCategories}
                                    handleTableCell={handleTableCell}
                                  />
                                ))}
                                <td>
                                  <input
                                    className="fa-cell-input"
                                    value={row.errorMessage || ''}
                                    onChange={e => handleTableCell('relevantProcesses', i, 'errorMessage', e.target.value)}
                                    style={{ minWidth: 110 }}
                                  />
                                </td>
                                <td>
                                  <button className="fa-btn-del" onClick={() => delRow('relevantProcesses', i)}>×</button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <button className="fa-btn-add" onClick={() => addRow('relevantProcesses', newProcessRow)}>
                        + Add process row
                      </button>
                    </div>
                  </div>
                )}

                {/* ╔══════════════ TAB 5: Purchased Precursors (edit-mode only) ══════════════╗ */}
                {!isCreationMode && effectiveActiveTab === 't5' && (
                  <div className="fa-tab-panel">
                    <div className="fa-note">
                      List all precursors produced OUTSIDE the installation (purchased) and consumed within it.
                      Also list the country in which each precursor was produced and the relevant production
                      routes, if known.
                    </div>
                    <div className="fa-warning-note">
                     If the emission data for your precursors is unavailable, it is essential to provide the CN code so that a default value can be used.
                    </div>
                    <div className="fa-table-wrap">
                      <table className="fa-table">
                       <thead>
                        <tr>
                          <th rowSpan={2} style={{ verticalAlign: 'bottom' }}>ID</th>
                          <th rowSpan={2} style={{ verticalAlign: 'bottom', minWidth: 160 }}>
                            Goods category
                            <div style={{ fontSize: 9, fontWeight: 400, opacity: 0.7, marginTop: 2 }}>
                              e.g. "Crude steel"
                            </div>
                          </th>
                          <th rowSpan={2} style={{ verticalAlign: 'bottom', minWidth: 110 }}>CN code</th>
                          <th rowSpan={2} style={{ verticalAlign: 'bottom', minWidth: 180 }}>Country</th>
                          <th rowSpan={2} style={{ verticalAlign: 'bottom', minWidth: 130 }}>Name</th>
                          <th colSpan={5} className="fa-th-group">Production routes (at origin)</th>
                          <th rowSpan={2} style={{ verticalAlign: 'bottom', minWidth: 90 }}>Error</th>
                          <th rowSpan={2} style={{ verticalAlign: 'bottom' }}>Actions</th>
                        </tr>
                          <tr>
                            {['Route 1','Route 2','Route 3','Route 4','Route 5'].map(r => (
                              <th key={r} style={{ background: '#4d5358', color: '#fff', fontSize: 10, minWidth: 110 }}>{r}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {(formData.purchasedPrecursors || []).map((row, i) => {
                            const precRoutes = ROUTES_BY_CATEGORY[row.goodsCategory]
                              || ROUTES_BY_CATEGORY[row.name]
                              || [];
                            return (
                              <tr key={row._uuid || i}>
                                <td className="fa-id-cell">{row.id}</td>
                                <td>
                                  <select
                                    className="fa-cell-select"
                                    value={row.goodsCategory || ''}
                                    onChange={e => handleTableCell('purchasedPrecursors', i, 'goodsCategory', e.target.value)}
                                    style={{ minWidth: 155 }}
                                  >
                                    <option value="">Select category...</option>
                                    {GOODS_NAMES.map(g => (
                                      <option key={g} value={g}>{g}</option>
                                    ))}
                                  </select>
                                </td>
                                <CNCodeSelect row={row} rowIndex={i} handleTableCell={handleTableCell} />
                                <td style={{ minWidth: 180 }}>
                                  <CountryAutocomplete
                                    value={row.countryCode || ''}
                                    onChange={(code) =>
                                      handleTableCell('purchasedPrecursors', i, 'countryCode', code)
                                    }
                                    variant="standard"
                                    placeholder="Country..."
                                    fullWidth
                                  />
                                </td>
                                <td>
                                  <input
                                    className="fa-cell-input"
                                    value={row.name || ''}
                                    onChange={e => handleTableCell('purchasedPrecursors', i, 'name', e.target.value)}
                                    placeholder="Precursor name..."
                                    style={{ minWidth: 130 }}
                                  />
                                </td>
                                {['route1','route2','route3','route4','route5'].map(f => (
                                  <RouteSelect
                                    key={f} row={row} rowIndex={i}
                                    table="purchasedPrecursors" field={f}
                                    options={precRoutes}
                                    handleTableCell={handleTableCell}
                                  />
                                ))}
                                <td>
                                  <input
                                    className="fa-cell-input"
                                    value={row.error || ''}
                                    onChange={e => handleTableCell('purchasedPrecursors', i, 'error', e.target.value)}
                                    style={{ minWidth: 90 }}
                                  />
                                </td>
                                <td>
                                  <button className="fa-btn-del" onClick={() => delRow('purchasedPrecursors', i)}>×</button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    <button className="fa-btn-add" onClick={() => addRow('purchasedPrecursors', newPrecursorRow)}>
                      + Add precursor row
                    </button>
                  </div>
                )}

                {/* Footer */}
                <div className="fa-footer-bar">
                  <div className="fa-footer-meta">
                    {isCreationMode ? (
                      <>
                        <span>Nothing is saved until you click Save &amp; Continue.</span>
                        <button
                          className="fa-btn-cancel"
                          onClick={handleCancel}
                          disabled={saving}
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <>
                        <span>
                          Overall progress: <strong>{calculateFormAProgress(formData).progress}%</strong>
                        </span>
                        {effectiveActiveTab !== 't1' && (
                          <button
                            className="fa-btn-prev"
                            onClick={handlePrevTab}
                            disabled={saving}
                            style={{ marginLeft: 8 }}
                          >
                            ← Previous tab
                          </button>
                        )}
                      </>
                    )}
                  </div>

                  <button
                    className={`fa-btn-save ${isCreationMode ? 'fa-btn-save-primary' : ''}`}
                    onClick={handleSave}
                    disabled={saving}
                  >
                    {saveLabel}
                  </button>
                </div>
              </div>

              {toast && (
                <div className={`fa-toast ${toast.type === 'error' ? 'error' : ''}`}>
                  {toast.type === 'error' ? '×' : '✓'} {toast.msg}
                </div>
              )}
            </div>
          </LocalizationProvider>
        </Box>
      </Box>
    </>
  );
};

export default FormA;
